<template>
  <div class="shell" :class="{ 'nav-open': navOpen }">
    <SideNav :collapsed="navCollapsed" :open="navOpen" @toggle="toggleNav" @navigate="closeNav" />
    <div v-if="navOpen" class="nav-backdrop" @click="closeNav"></div>
    <main :class="['main', { collapsed }]">
      <button class="icon-btn menu-btn" @click="toggleNav" aria-label="Toggle menu">
        <span class="bar"></span>
        <span class="bar"></span>
        <span class="bar"></span>
      </button>
      <nav class="breadcrumbs" aria-label="Breadcrumb">
        <template v-for="(crumb, index) in breadcrumbs" :key="`${crumb.label}-${index}`">
          <router-link
            v-if="crumb.to && index < breadcrumbs.length - 1"
            class="crumb"
            :to="crumb.to"
          >
            {{ crumb.label }}
          </router-link>
          <span v-else class="crumb current">{{ crumb.label }}</span>
          <span v-if="index < breadcrumbs.length - 1" class="crumb-sep">/</span>
        </template>
      </nav>
      <section class="content">
        <router-view />
      </section>
    </main>
  </div>
</template>

<script>
import SideNav from "./SideNav.vue";
import { logout } from "../../services/auth";

export default {
  name: "AppShell",
  components: { SideNav },
  data() {
    return {
      collapsed: false,
      navOpen: false,
      isMobile: false,
      vehicleBreadcrumbLabel: "",
    };
  },
  provide() {
    return {
      setVehicleBreadcrumb: this.setVehicleBreadcrumb,
      clearVehicleBreadcrumb: this.clearVehicleBreadcrumb,
    };
  },
  computed: {
    navCollapsed() {
      return this.collapsed && !this.isMobile;
    },
    breadcrumbs() {
      const name = this.$route?.name;
      const crumbs = [{ label: "Dashboard", to: "/dashboard/overview" }];
      if (name === "overview") {
        crumbs.push({ label: "Overview" });
      } else if (name === "asset-detail") {
        crumbs.push({ label: "Assets", to: "/dashboard/overview" });
        crumbs.push({ label: this.$route?.params?.symbol || "Detalle" });
      } else if (name === "external-assets") {
        crumbs.push({ label: "Activos externos" });
      } else if (name === "expenses") {
        crumbs.push({ label: "Gastos" });
      } else if (name === "profile") {
        crumbs.push({ label: "Perfil" });
      } else if (name === "documents") {
        crumbs.push({ label: "Documentos" });
      } else if (name === "health") {
        crumbs.push({ label: "Salud" });
      } else if (name === "tasks-board") {
        crumbs.push({ label: "Tareas" });
        crumbs.push({ label: "Tablero" });
      } else if (name === "maintenance-vehicles") {
        crumbs.push({ label: "Vehículos" });
      } else if (name === "vehicle-detail") {
        crumbs.push({ label: "Vehículos", to: "/dashboard/maintenance/vehicles" });
        const label = this.vehicleBreadcrumbLabel?.trim();
        crumbs.push({ label: label || `Historial ${this.$route?.params?.id || ""}`.trim() });
      } else if (name === "maintenance-home") {
        crumbs.push({ label: "Mantenimientos" });
        crumbs.push({ label: "Casa" });
      } else if (name === "maintenance-taxes") {
        crumbs.push({ label: "Mantenimientos" });
        crumbs.push({ label: "Impuestos" });
      } else if (name === "admin-files") {
        crumbs.push({ label: "Administración" });
        crumbs.push({ label: "Explorador de archivos" });
      }
      return crumbs;
    },
    pageTitle() {
      const name = this.$route?.name;
      if (name === "overview") return "Overview financiero";
      if (name === "asset-detail") return `Detalle de ${this.$route?.params?.symbol || ""}`;
      if (name === "expenses") return "Gastos previstos";
      if (name === "profile") return "Perfil de usuario";
      if (name === "maintenance-vehicles") return "Mantenimientos de vehículos";
      if (name === "maintenance-home") return "Mantenimientos de casa";
      if (name === "maintenance-taxes") return "Impuestos";
      return "Dashboard";
    },
  },
  mounted() {
    const stored = window.localStorage.getItem("cerebro_nav_collapsed");
    this.collapsed = stored === "true";
    this.updateViewport();
    window.addEventListener("resize", this.updateViewport);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.updateViewport);
    this.unlockBodyScroll();
  },
  watch: {
    navOpen() {
      this.syncBodyScroll();
    },
    "$route.name"(name) {
      if (name !== "vehicle-detail") {
        this.clearVehicleBreadcrumb();
      }
    },
  },
  methods: {
    setVehicleBreadcrumb(label) {
      this.vehicleBreadcrumbLabel = label || "";
    },
    clearVehicleBreadcrumb() {
      this.vehicleBreadcrumbLabel = "";
    },
    syncBodyScroll() {
      if (!this.isMobile) {
        this.unlockBodyScroll();
        return;
      }
      if (this.navOpen) {
        document.body.style.overflow = "hidden";
      } else {
        this.unlockBodyScroll();
      }
    },
    unlockBodyScroll() {
      document.body.style.overflow = "";
    },
    updateViewport() {
      this.isMobile = window.innerWidth <= 900;
      if (!this.isMobile) {
        this.navOpen = false;
        this.unlockBodyScroll();
      }
    },
    toggleNav() {
      if (this.isMobile) {
        this.navOpen = !this.navOpen;
        return;
      }
      this.collapsed = !this.collapsed;
      window.localStorage.setItem("cerebro_nav_collapsed", String(this.collapsed));
    },
    closeNav() {
      if (this.isMobile) {
        this.navOpen = false;
      }
    },
    async logout() {
      await logout();
      this.$router.push("/login");
    },
  },
};
</script>

<style scoped>
.shell {
  display: grid;
  grid-template-columns: auto 1fr;
  min-height: 100vh;
}

.nav-backdrop {
  display: none;
}

.main {
  padding: 24px 36px 60px;
  transition: padding-left 0.3s ease;
  position: relative;
}

.main.collapsed {
  padding-left: 24px;
}

.icon-btn {
  padding: 10px;
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 12px;
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  cursor: pointer;
  gap: 4px;
}

.menu-btn {
  position: sticky;
  top: 18px;
  z-index: 2;
}

.breadcrumbs {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 12px 0 6px;
  font-size: 11px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--muted);
  flex-wrap: wrap;
}

.crumb {
  color: var(--muted);
  transition: color 0.2s ease;
}

.crumb:hover {
  color: var(--text);
}

.crumb.current {
  color: var(--text);
  font-weight: 600;
}

.crumb-sep {
  color: rgba(148, 163, 184, 0.35);
}

.bar {
  width: 18px;
  height: 2px;
  background: var(--text);
  display: block;
}

.content {
  margin-top: 16px;
}

@media (max-width: 900px) {
  .shell {
    grid-template-columns: 1fr;
  }

  .nav-backdrop {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(6, 10, 18, 0.6);
    z-index: 8;
  }

  .main {
    padding: 20px;
  }

  .topbar {
    flex-wrap: wrap;
  }
}
</style>
